import {
  ApolloClient,
  DocumentNode,
  NormalizedCacheObject,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
  useQuery,
} from "@apollo/client";
import { askrClients } from "providers/AskrProvider";
import { midgardClients } from "providers/MidgardProvider";

type GraphQLServiceType = "midgard" | "askr" | undefined;

type TenantQueryOptions<TData, TVariables> = QueryHookOptions<
  TData,
  TVariables
> & {
  tenant: string | undefined;
  service: GraphQLServiceType;
};

export const useTenantQuery = <TData = any, TVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  { tenant, service, ...options }: TenantQueryOptions<TData, TVariables> = {
    tenant: undefined,
    service: undefined,
  }
): QueryResult<TData, TVariables> => {
  const client = getClients(service, tenant);

  /**
   * These pathnames are interpreted as valid tenants by accident when using the
   * `useGetCustomerKey` hook. Unfortunately, there is no simple way to resolve this
   * issue without a major refactor of the routing. And querying for all tenants from the `useFetchTenants`
   * hook to validate against adds extra complexity and an additional failure point that is unlikely to be ideal either.
   *
   * The real solution is likely to refactor the /:tenant/* paths to /tenant/:tenant/* and then only run tenant-specific
   * queries when on those subpaths. At least that way it can be assumed that any given tenant is a valid tenant.
   * Currently, we accidentally assume all paths at the base path are a valid tenant, which is not the case.
   *
   * The current solution simply skips queries where an invalid tenant has been parsed from the path.
   * The `callback` and `backtoflight` pathnames have been causing trouble with users trying to access reports from Flight,
   * especially when they're on slower internet connections, as they stay on these paths for long enough that the
   *
   * This is, for now, likely the simplest solution that can solve the immediate problem without a large refactor or additional complexity.
   */
  const isNotValidTenant = !tenant || tenant === "";
  const shouldSkip = !service || isNotValidTenant;

  return useQuery(query, {
    ...options,
    client,
    // Skip the query if the tenant or service doesn't exist, but allow overriding skip for consumers
    skip: shouldSkip || options.skip,
  });
};

const getClients = (
  service: GraphQLServiceType,
  tenant: string | undefined
): ApolloClient<NormalizedCacheObject> | undefined => {
  if (!tenant) {
    return undefined;
  }
  switch (service) {
    case "askr":
      return askrClients.get(tenant);
    case "midgard":
      return midgardClients.get(tenant);
    default:
      return undefined;
  }
};
